import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Chart基础容器组件
 * 提供图表的基础布局、标题、说明等通用功能
 *
 * @example
 * ```tsx
 * // 基本用法
 * <Chart title="销售数据" description="本月销售统计">
 *   <BarChart data={salesData} />
 * </Chart>
 *
 * // 带工具栏的图表
 * <Chart
 *   title="用量统计"
 *   toolbar={<RefreshButton />}
 *   loading={isLoading}
 * >
 *   <LineChart data={usageData} />
 * </Chart>
 *
 * // 响应式图表
 * <Chart title="趋势分析" responsive>
 *   <AreaChart data={trendData} />
 * </Chart>
 * ```
 */
import { forwardRef } from 'react';
import { cn } from '../../utils/cn';
const BORDER_COLOR_MAP = {
    default: 'border-purple-500',
    'royal-blue': 'border-interactive-royal-blue',
    'sunset-orange': 'border-interactive-sunset-orange',
    'beach-orange': 'border-static-beach-orange-200',
    'emerald-flash': 'border-interactive-emerald-flash',
    'electric-yellow': 'border-interactive-electric-yellow',
    'coral-red': 'border-interactive-coral-red',
};
/**
 * 获取尺寸相关的CSS类
 */
const getSizeClasses = (size) => {
    switch (size) {
        case 'small':
            return 'h-48 min-h-48';
        case 'medium':
            return 'h-64 min-h-64';
        case 'large':
            return 'h-80 min-h-80';
        case 'auto':
            return 'h-auto min-h-32';
        default:
            return 'h-64 min-h-64';
    }
};
/**
 * 获取主题相关的CSS类
 */
const getThemeClasses = (theme) => {
    switch (theme) {
        case 'dark':
            return 'bg-gray-900 text-white';
        case 'light':
            return 'text-gray-900';
        case 'auto':
            return 'text-gray-900 dark:bg-gray-900 dark:text-white';
        default:
            return 'text-gray-900';
    }
};
/**
 * Chart基础容器组件
 */
export const Chart = forwardRef(({ title, description, size = 'medium', theme = 'light', responsive = true, loading = false, loadingText = 'Loading...', toolbar, footer, children, className, colorVariant = 'default', error, empty = false, emptyText = 'No data available', ...props }, ref) => {
    const borderColorClass = BORDER_COLOR_MAP[colorVariant] || BORDER_COLOR_MAP.default;
    const containerClasses = cn('bg-static-eggshell-50 rounded-lg overflow-hidden border-2 p-6 flex flex-col h-full', borderColorClass, getThemeClasses(theme), // 允许主题覆盖背景色
    responsive && 'w-full', className);
    const chartContentClasses = cn('relative flex-1', // 保持相对定位以支持网格线等
    getSizeClasses(size), responsive && 'w-full');
    return (_jsxs("div", { ref: ref, className: containerClasses, ...props, children: [(title || toolbar) && (_jsxs("div", { className: "flex items-start justify-between mb-4", children: [_jsxs("div", { children: [title && (_jsx("h3", { className: "text-lg font-semibold text-gray-600 dark:text-white", children: title })), description && (_jsx("p", { className: "text-sm text-gray-600 dark:text-gray-400 mt-1", children: description }))] }), toolbar && _jsx("div", { className: "flex items-center gap-2", children: toolbar })] })), _jsx("div", { className: chartContentClasses, children: error ? (
                /* 错误状态 */
                _jsx("div", { className: "flex items-center justify-center h-full", children: _jsxs("div", { className: "text-center", children: [_jsx("h2", { className: "text-xl font-semibold text-interactive-coral-red mb-2", children: "Failed to load" }), _jsx("p", { className: "text-gray-600 dark:text-gray-400", children: error })] }) })) : loading ? (
                /* 加载状态 */
                _jsxs("div", { className: "w-full h-full space-y-4 animate-pulse", children: [_jsx("div", { className: "h-8 bg-gray-200 rounded w-1/3" }), _jsxs("div", { className: "flex-1 space-y-2", children: [_jsx("div", { className: "h-4 bg-gray-300 rounded" }), _jsx("div", { className: "h-4 bg-gray-300 rounded w-5/6" })] })] })) : empty ? (
                /* 空数据状态 */
                _jsx("div", { className: "flex items-center justify-center h-full", children: _jsx("div", { className: "text-center", children: _jsx("h4", { className: "text-lg font-medium text-gray-500 dark:text-gray-400", children: emptyText }) }) })) : (
                /* 正常的图表内容 */
                children) }), footer && _jsx("div", { className: "dark:border-gray-700", children: footer })] }));
});
Chart.displayName = 'Chart';
export default Chart;
//# sourceMappingURL=Chart.js.map